<!--
This component manages and tracks background updates, providing real-time feedback to users about the status of the operation.
It handles scenarios where updates take a long time, and ensures users are informed if they return to the page later.
Example: Update of pay rate of 1500 members on the members page.
This component can be removed after feature flag background_tasks_web_sockets is no longer necessary
-->
<template>
  <AppAlert
    v-if="showAlert"
    :type="alertType"
    :class="alertClass"
    :message="alertMessage"
    icon-classes="hi hi-info"
  />
</template>

<script>
import { BackgroundTasksResource } from '~/resources';
import { mapGetters } from 'vuex';

// Key for storing the bulk update tracker state in localStorage
const TRACKER_STATE = 'bulkUpdateTrackerState';
// Key for storing the message to be displayed after page reload in localStorage
const MESSAGE_AFTER_RELOAD = 'messageAfterReload';
// Maximum time (in milliseconds) to consider an update still relevant (15 minutes)
const MAX_UPDATE_RELEVANCE_TIME = 15 * 60 * 1000;
// Alert display time
const ALERT_DISPLAY_DURATION = 5000; // 5 seconds

export default {
  name: 'BulkUpdateTracker',
  props: {
    alertClass: {
      type: String,
      default: undefined,
    },
    needToReload: {
      // use in case the page content is only updated after a reload
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    queue: {},
    showAlert: false,
    alertType: 'warning',
    alertMessage: '',
    alertTimer: null,
  }),
  computed: {
    ...mapGetters(['organizationId']),
    queued() {
      return !!this.queue.queueId;
    },
  },
  mounted() {
    this.trackIfNeeded();

    if (this.needToReload) {
      this.displayStatusAfterReload();
    }
  },
  beforeUnmount() {
    this.clearAlertTimer();
  },
  methods: {
    // To be used on a parent component, in case we want to know
    // if the operation is still running without touching the backend
    hasOngoingOperation() {
      const storedState = localStorage.getItem(TRACKER_STATE);

      if (storedState) {
        const { timestamp } = JSON.parse(storedState);
        const elapsedTime = Date.now() - timestamp;

        return elapsedTime < MAX_UPDATE_RELEVANCE_TIME;
      }

      return false;
    },
    // In case the user visits another page and comes back we
    // track the operation so he knows what happened, in case 15 minutes have passed we ignore it
    trackIfNeeded() {
      const storedState = localStorage.getItem(TRACKER_STATE);

      if (storedState) {
        const { backgroundId, timestamp, action } = JSON.parse(storedState);
        const elapsedTime = Date.now() - timestamp;

        if (elapsedTime < MAX_UPDATE_RELEVANCE_TIME) {
          this.trackBulkUpdate(backgroundId, action, true);
        } else {
          localStorage.removeItem(TRACKER_STATE);
        }
      }
    },
    // Some UI operations don't use the vue native dialog
    // and are reloading the page to update the page content
    displayStatusAfterReload() {
      const successMessage = localStorage.getItem(MESSAGE_AFTER_RELOAD);

      if (successMessage) {
        this.displayAlert('success', successMessage);
        localStorage.removeItem(MESSAGE_AFTER_RELOAD);
      }
    },
    clearQueued(queueId) {
      if (this.queue.queueId === queueId) {
        this.queue = {};
      }
    },
    async trackBulkUpdate(backgroundId, action, calledOnPageLoad = false) {
      const queueId = Date.now();

      this.queue = { queueId, type: 'backgrounded' };

      if (!calledOnPageLoad) {
        this.displayAlert(
          'warning',
          `The update is in progress. You can leave this page, and you will be notified when you return.`,
        );
      } else {
        this.displayAlert(
          'warning',
          `The update is still running. You can leave this page, and you will be notified when you return.`,
        );
      }

      localStorage.setItem(
        TRACKER_STATE,
        JSON.stringify({
          backgroundId,
          timestamp: Date.now(),
          action,
        }),
      );

      const checkStatus = async () => {
        const { success, ready, confirm_update } = await BackgroundTasksResource.get({
          organizationId: this.organizationId,
          id: backgroundId,
        });

        if (!success) {
          this.handleError(queueId);
        } else if (ready && confirm_update) {
          this.handleSuccess(queueId);
        } else if (ready) {
          this.handleNoUpdate(queueId);
        } else {
          // Continue checking status
          setTimeout(checkStatus, 5000);
        }
      };

      // Perform the initial check immediately if called on page load (user is coming back), otherwise wait 5 seconds
      if (calledOnPageLoad) {
        await checkStatus();
      } else {
        setTimeout(checkStatus, 5000);
      }
    },
    handleError(queueId) {
      const message = `Unable to perform the update for the selected records. Please ensure that you have provided the correct input values and try again.`;

      this.displayAlert('danger', message);
      this.$emit('error');
      this.clearQueued(queueId);
      localStorage.removeItem(TRACKER_STATE);
    },
    handleSuccess(queueId) {
      const message = `Update completed successfully.`;

      this.clearQueued(queueId);
      localStorage.removeItem(TRACKER_STATE);
      this.$emit('success');

      if (this.needToReload) {
        localStorage.setItem(MESSAGE_AFTER_RELOAD, message);
        window.location.reload();
      } else {
        this.displayAlert('success', message.slice(0, -1)); // minor UI detail, app messages don't have the '.'
      }
    },
    handleNoUpdate(queueId) {
      const message = `No records where affected by the update.`;

      this.displayAlert('info', message);
      this.$emit('no-update');
      this.clearQueued(queueId);
      localStorage.removeItem(TRACKER_STATE);
    },
    setAlertTimer() {
      this.alertTimer = setTimeout(() => {
        this.showAlert = false;
      }, ALERT_DISPLAY_DURATION);
    },
    clearAlertTimer() {
      if (this.alertTimer) {
        clearTimeout(this.alertTimer);
        this.alertTimer = null;
      }
    },
    displayAlert(type, message) {
      this.clearAlertTimer();
      this.showAlert = true;
      this.alertType = type;
      this.alertMessage = message;

      if (type !== 'warning') {
        this.setAlertTimer();
      }
    },
  },
};
</script>
